import React, { useState, useEffect } from 'react';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.css';
import api from '../../../../api';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { TailSpin } from 'react-loader-spinner';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

const CompareDateRanges = ({ dataType }) => {
  const { accessToken } = useAuth();

  // State for date ranges and fetched data
  const [dateRange1, setDateRange1] = useState([null, null]);
  const [dateRange2, setDateRange2] = useState([null, null]);
  const [range1Data, setRange1Data] = useState(null);
  const [range2Data, setRange2Data] = useState(null);
  const [difference, setDifference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getWeekdayRangeForDate = (selectedDate) => {
    const date = new Date(selectedDate);
    const day = date.getDay();
    const diffToMonday = day === 0 ? 6 : day - 1; // If Sunday (0), go back 6 days; else subtract (day - 1)
    const monday = new Date(date);
    monday.setDate(date.getDate() - diffToMonday);
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    return [monday, friday];
  };

  const formatDate = (date) => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const fetchData = async (startDate1, endDate1, startDate2, endDate2) => {
    if (!accessToken) return;
    setLoading(true);
    try {
      let baseUrls = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrls}/managerDashboard/getCustomRangeComparison`, {
        params: {
          selectDateRange1: `${formatDate(startDate1)},${formatDate(endDate1)}`,
          selectDateRange2: `${formatDate(startDate2)},${formatDate(endDate2)}`,
          type: dataType,
        },
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const { firstData, secondRangeData, difference } = response.data;
      setRange1Data(firstData);
      setRange2Data(secondRangeData);
      setDifference(difference);
    } catch (err) {
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date();
    const [currentMonday, currentFriday] = getWeekdayRangeForDate(today);
    const previousWeekDate = new Date(today);
    previousWeekDate.setDate(today.getDate() - 7);
    const [previousMonday, previousFriday] = getWeekdayRangeForDate(previousWeekDate);

    setDateRange1([previousMonday, previousFriday]);
    setDateRange2([currentMonday, currentFriday]);

    fetchData(previousMonday, previousFriday, currentMonday, currentFriday);
  }, [accessToken, dataType]);

  const handleDateRange1Change = (dates) => {
    if (dates.length > 0) {
      const [monday, friday] = getWeekdayRangeForDate(dates[0]);
      setDateRange1([monday, friday]);
      fetchData(monday, friday, dateRange2[0], dateRange2[1]);
    }
  };

  const handleDateRange2Change = (dates) => {
    if (dates.length > 0) {
      const [monday, friday] = getWeekdayRangeForDate(dates[0]);
      setDateRange2([monday, friday]);
      fetchData(dateRange1[0], dateRange1[1], monday, friday);
    }
  };

  return (
    <div className="col-span-2">
      <div className="p-6 rounded-2xl shadow-xl bg-white border border-[#1A2B5C]/20 transition-all hover:shadow-2xl">
        <h6 className="text-xl font-bold text-white mb-6 flex items-center justify-center bg-gradient-to-r from-[#1A2B5C] to-blue-900 p-3 rounded-lg shadow-md">
          📅 Compare Date Ranges
        </h6>
        <div className="grid xl:grid-cols-2 gap-6">
          <div className="p-4 bg-gray-50 rounded-lg shadow-md border border-gray-300 hover:shadow-lg transition">
            <h6 className="text-lg font-semibold text-gray-800 mb-2">Week 1</h6>
            <Flatpickr
              value={dateRange1}
              onChange={handleDateRange1Change}
              options={{ mode: 'single', dateFormat: 'm/d/y', allowInput: true }}
              className="w-full border border-gray-300 rounded-lg p-2 text-md focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
            />
          </div>

          <div className="p-4 bg-gray-50 rounded-lg shadow-md border border-gray-300 hover:shadow-lg transition">
            <h6 className="text-lg font-semibold text-gray-800 mb-2">Week 2</h6>
            <Flatpickr
              value={dateRange2}
              onChange={handleDateRange2Change}
              options={{ mode: 'single', dateFormat: 'm/d/y', allowInput: true }}
              className="w-full border border-gray-300 rounded-lg p-2 text-md focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
            />
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center mt-6">
            <TailSpin height="50" width="50" color="blue" ariaLabel="loading" />
          </div>
        )}

        {error && <div className="mt-4 text-red-500 text-center font-semibold">{error}</div>}

        {range1Data && range2Data && difference && (
          <div className="mt-6 p-5 border rounded-xl bg-gray-100 shadow-md">
            <table className="w-full border-collapse border border-gray-300 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-gradient-to-r from-gray-600 to-gray-800 text-white">
                  <th className="border border-gray-400 p-3">Category</th>
                  <th className="border border-gray-400 p-3">Week 1</th>
                  <th className="border border-gray-400 p-3">Week 2</th>
                  <th className="border border-gray-400 p-3">Difference</th>
                </tr>
              </thead>
              <tbody>
                <tr className="text-center bg-gray-50 hover:bg-gray-100 transition">
                  <td className="border border-gray-300 p-3">Production Percent</td>
                  <td className="border border-gray-300 p-3">{range1Data.productionPercent}</td>
                  <td className="border border-gray-300 p-3">{range2Data.productionPercent}</td>
                  <td className="border border-gray-300 p-3">{difference.productionPercent}</td>
                </tr>
                <tr className="text-center bg-gray-50 hover:bg-gray-100 transition">
                  <td className="border border-gray-300 p-3">Achieved</td>
                  <td className="border border-gray-300 p-3">{range1Data.achieved}</td>
                  <td className="border border-gray-300 p-3">{range2Data.achieved}</td>
                  <td className="border border-gray-300 p-3">{difference.achieved}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompareDateRanges;
